import { CSSResultGroup, html, TemplateResult } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import componentStyles from './idb-highcharts-target.css.lit';
import { OutlineElement } from '../../base/outline-element/outline-element';
import * as Highcharts from 'highcharts';


declare module 'highcharts' {
  interface Chart {
    hoverLabel?: Highcharts.SVGElement;
  }
}
interface CustomPointOptions {
  target_value: string;
  target_descrip: string;
  progress_value: string;
  yr: number;
}

interface CustomYAxis {
  min: number;
  max: number;
  tickInterval: number;
  allowDecimals: boolean;
}

interface CustomPlotLineOptions {
  value: number;
}

/**
 * The IDB Highcharts component.
 * @element idb-highcharts target
 * @extends OutlineElement
 */
@customElement('idb-highcharts-target')
export class IdbToggleView extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles];

  @property({ type: String, attribute: 'organization' })
  organization: string;

  @property({ type: String, attribute: 'target-label' })
  title: string;

  @property({ type: String, attribute: 'progress-label' })
  progress: string;

  @property({ type: Object, attribute: 'chart-y-axis' })
  chartYAxis: CustomYAxis;

  @property({ type: Object, attribute: 'chart-data' })
  chartData: CustomPointOptions[];

  @property({ type: String, attribute: 'unit-measure' })
  unitMeasure: string;

  private chart?: Highcharts.Chart;

  firstUpdated() {
    this.createChart();
  }

  updated(changedProperties: Map<string | number | symbol, unknown>) {
    if (changedProperties.has('chartData') && this.chart) {
      this.chart.update(this.createSeries());
    }
  }

  // Método para inicializar el gráfico
  private createChart() {
    const container = this.shadowRoot?.getElementById(
      'chart-container'
    ) as HTMLElement;

    if (container) {
      this.chart = Highcharts.chart(container, {
        ...this.defaultOptions(),
        ...this.createSeries(),
      });
    }
  }

  render(): TemplateResult {
    return html`<div id="chart-container"></div>`;
  }

  private createSeries(): Highcharts.Options {
    if (this.chartData) {
      const unitMeasure = this.unitMeasure ? this.unitMeasure.toLowerCase() : '';
      const data = this.chartData.map(item => {
        return {
          y: parseFloat(item.progress_value),
          label: item.progress_value
        };
      });
      const title = this.title;
      const progress = this.progress;

      const years = this.chartData.map(item => String(item.yr));
      const target =  this.chartData[years.length -1].target_value ? parseFloat(this.chartData[years.length -1].target_value) : '';
      const targetDescription = this.chartData[0].target_descrip;

      return {
        chart: {
          style: {
            fontFamily: 'var(--ff-display)',
          },
          events: {
            load: function() {
              const chart = this;
              chart.renderer
                .text(
                  `<div class="subtitle">
                    ${target ? `<span class="target">${title}</span>`:``}
                    <span class="line">${progress}</span>
                  </div>
                  `,
                  chart.plotLeft,
                  chart.plotTop + chart.plotHeight,
                  true
                )
                .add();
              chart.renderer.rect(
                chart.plotLeft,
                chart.plotTop,
                chart.plotWidth,
                chart.plotHeight
              )
              .attr({
                fill: 'transparent',
                cursor: 'default',
                zIndex: 6
              }).on('mouseover', function () {

                (chart.yAxis[0] as Partial<Highcharts.Axis & { plotLinesAndBands?:  Highcharts.AxisPlotBandsOptions[] }>).plotLinesAndBands?.forEach(
                    function (plotLine: Partial<Highcharts.AxisPlotBandsOptions & { options?: CustomPlotLineOptions, svgElem?: Highcharts.SVGElement} >) {

                  if (plotLine.options?.value === target && plotLine.svgElem) {
                    const bbox = plotLine.svgElem.getBBox();
                    const xPosition = bbox.x + 5;
                    const yPosition = bbox.y - 8 + bbox.height / 2;
                    if (!chart.hoverLabel) {
                      chart.hoverLabel = chart.renderer.text(targetDescription, xPosition, yPosition)
                        .attr({
                          zIndex: 7,
                        })
                        .css({
                          color: 'red',
                          fontSize: 'var(--fs-sm)',
                        })
                        .add();
                    } else {
                      chart.hoverLabel.attr({
                        x: xPosition,
                        y: yPosition,
                        text: targetDescription,
                      }).show();
                    }
                  }
                });
              }).on('mouseout', function () {
                if (chart.hoverLabel) {
                  chart.hoverLabel.hide();
                }
              })
              .add();
            }
          },        
        },
        subtitle: 
          {
            text: '---' + title + ' ' + targetDescription,
            align: 'left',
            verticalAlign: 'bottom',
            x: 15,
            y: 20,
            style: {
              color: 'transparent',
              border: 'solid 1px',
              fontSize: 'var(--fs-base)',
            } 
          } as Partial<Highcharts.SubtitleOptions & { align?: string }>,
        xAxis: {
          gridLineWidth: 0,
          categories: years,
          title: {
            text: '',
          },
          lineWidth: 0,
        },
        yAxis: {
          min: this.chartYAxis?.min,
          max: this.chartYAxis?.max,
          tickInterval: this.chartYAxis?.tickInterval,
          gridLineWidth: 1,
          title: {
            text: '',
          },
          lineWidth: 0,
          lineColor: '#000',
          plotLines: [
            {
              value: target,
              color: 'red',
              dashStyle: 'ShortDash',
              width: 2,
              zIndex: 7,
              label: {
                text: '',
                align: 'left',
                style: {
                  color: 'red',
                },
              },
            },
          ],
        },
        plotOptions: {
          series: {
            marker: {
              radius: 3,
            },
          },
        },
        tooltip: {
          headerFormat: '',
          formatter: function (this: Highcharts.Point) {
            if (unitMeasure === 'percent' || unitMeasure === 'porcentagem' || unitMeasure === 'porcentaje' || unitMeasure === 'pourcentage') {
              return `${this.options.label}%`;
            }else {
              return `${this.options.label}`;
            }
          },
        },
        series: [
          {
            type: 'line',
            name: '',
            keys: ['y'],
            color: '#0573ae',
            data,
            clip: false,
            zIndex: 6,
            dataLabels: {
              enabled: false,
              style: {
                fontSize: 'var(--fs-base)',
                color: '#888888',
                textOutline: '#ffffff',
              },
              formatter: function (this: Highcharts.Point) {
                return this.y + '%';
              },
            },
            showInLegend: false,
          },
        ],
      };
    }
    return {};
  }

  private defaultOptions(): Highcharts.Options {
    return {
      chart: {
        backgroundColor: 'transparent',
        type: 'line',
      },
      title: {
        text: '',
      },
      exporting: {
        enabled: false,
      },
      yAxis: {
        gridLineWidth: 0,
        title: {
          text: '',
        },
        lineWidth: 1,
        lineColor: '#000',
        plotLines: [
          {
            color: '#000',
          },
        ],
      },
      plotOptions: {
        series: {
          lineWidth: 9,
        },
      },
      tooltip: {
        enabled: false,
      },
      credits: {
        enabled: false,
      },
    };
  }
}
